import { motion } from 'framer-motion'
import { Users, Link, MousePointer, TrendingUp } from 'lucide-react'
import type { AnalyticsData } from './types'

interface OverviewStatsProps {
  analyticsData: AnalyticsData
}

export default function OverviewStats({ analyticsData }: OverviewStatsProps) {
  return (
    <>
      {/* Overview Stats */}
      <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Users
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {analyticsData.overview.totalUsers.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 dark:bg-gray-700">
            <div className="text-sm">
              <span className="font-medium text-green-600 dark:text-green-400">
                +{analyticsData.overview.usersGrowth}%
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {' '}
                this month
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Link className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total URLs
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {analyticsData.overview.totalUrls.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 dark:bg-gray-700">
            <div className="text-sm">
              <span className="font-medium text-green-600 dark:text-green-400">
                +{analyticsData.overview.urlsGrowth}%
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {' '}
                this month
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MousePointer className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                    Total Clicks
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {analyticsData.overview.totalClicks.toLocaleString()}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 dark:bg-gray-700">
            <div className="text-sm">
              <span className="font-medium text-green-600 dark:text-green-400">
                +{analyticsData.overview.clicksGrowth}%
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {' '}
                this month
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800"
        >
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="truncate text-sm font-medium text-gray-500 dark:text-gray-400">
                    Average CTR
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {analyticsData.overview.averageCtr}%
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3 dark:bg-gray-700">
            <div className="text-sm">
              <span className="text-gray-500 dark:text-gray-400">Today: </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {analyticsData.overview.todayClicks.toLocaleString()} clicks
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  )
}
